import React from 'react';
import { TrendingUp, Users, MousePointerClick, Target, ArrowUp, ArrowDown } from 'lucide-react';
import Icon from '../../../components/AppIcon';


const AnalyticsOverview = ({ analyticsData, auditItems, onRefresh }) => {
  // Calculate metrics from analytics data
  const calculateMetrics = () => {
    if (!analyticsData || analyticsData?.length === 0) {
      return {
        totalEvents: 0,
        conversions: 0,
        avgScore: 0,
        criticalIssues: 0
      };
    }

    const totalEvents = analyticsData?.length;
    const conversions = analyticsData?.filter(event => event?.conversion_tracked)?.length;
    const avgScore = auditItems?.length > 0
      ? Math.round(auditItems?.reduce((sum, item) => sum + (item?.score || 0), 0) / auditItems?.length)
      : 0;
    const criticalIssues = auditItems?.filter(item => item?.priority === 'critical' && item?.current_status === 'pending')?.length || 0;

    return { totalEvents, conversions, avgScore, criticalIssues };
  };

  const metrics = calculateMetrics();

  const statCards = [
    {
      title: 'Total Events Tracked',
      value: metrics?.totalEvents,
      change: '+12%',
      trend: 'up',
      icon: MousePointerClick,
      color: 'blue'
    },
    {
      title: 'Conversions',
      value: metrics?.conversions,
      change: '+8%',
      trend: 'up',
      icon: Target,
      color: 'green'
    },
    {
      title: 'Average SEO Score',
      value: `${metrics?.avgScore}%`,
      change: '+5%',
      trend: 'up',
      icon: TrendingUp,
      color: 'purple'
    },
    {
      title: 'Critical Issues',
      value: metrics?.criticalIssues,
      change: '-2',
      trend: 'down',
      icon: Users,
      color: 'red'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards?.map((stat, index) => {
          const Icon = stat?.icon;
          const TrendIcon = stat?.trend === 'up' ? ArrowUp : ArrowDown;
          
          return (
            <div key={index} className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-4">
                <div className={`w-12 h-12 rounded-lg bg-${stat?.color}-100 flex items-center justify-center`}>
                  <Icon className={`w-6 h-6 text-${stat?.color}-600`} />
                </div>
                <div className={`flex items-center gap-1 text-sm font-medium ${
                  stat?.trend === 'up' ? 'text-green-600' : 'text-red-600'
                }`}>
                  <TrendIcon className="w-4 h-4" />
                  <span>{stat?.change}</span>
                </div>
              </div>
              <h3 className="text-gray-600 text-sm font-medium mb-1">{stat?.title}</h3>
              <p className="text-3xl font-bold text-gray-900">{stat?.value}</p>
            </div>
          );
        })}
      </div>
      {/* Analytics Chart Placeholder */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900">30-Day Performance Trend</h2>
          <button
            onClick={onRefresh}
            className="px-4 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
          >
            Refresh Data
          </button>
        </div>
        
        {/* Simple Bar Chart Representation */}
        <div className="space-y-4">
          {[
            { label: 'Organic Traffic', value: 85, color: 'blue' },
            { label: 'Direct Traffic', value: 65, color: 'green' },
            { label: 'Referral Traffic', value: 45, color: 'purple' },
            { label: 'Social Traffic', value: 30, color: 'pink' }
          ]?.map((item, index) => (
            <div key={index} className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium text-gray-700">{item?.label}</span>
                <span className="text-gray-600">{item?.value}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className={`bg-${item?.color}-600 h-3 rounded-full transition-all duration-500`}
                  style={{ width: `${item?.value}%` }}
                ></div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Quick Insights */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Top Performing Pages</h3>
          <div className="space-y-3">
            {[
              { page: '/home', views: '2,450', bounce: '32%' },
              { page: '/services', views: '1,890', bounce: '28%' },
              { page: '/about', views: '1,234', bounce: '35%' }
            ]?.map((page, index) => (
              <div key={index} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                <span className="text-gray-700 font-medium">{page?.page}</span>
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-gray-600">{page?.views} views</span>
                  <span className="text-gray-600">{page?.bounce} bounce</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Recent Conversions</h3>
          <div className="space-y-3">
            {[
              { type: 'Phone Call', time: '2 min ago', value: '$500' },
              { type: 'Form Submission', time: '15 min ago', value: '$350' },
              { type: 'Chat Inquiry', time: '1 hour ago', value: '$200' }
            ]?.map((conversion, index) => (
              <div key={index} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                <div>
                  <p className="text-gray-700 font-medium">{conversion?.type}</p>
                  <p className="text-sm text-gray-500">{conversion?.time}</p>
                </div>
                <span className="text-green-600 font-bold">{conversion?.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsOverview;